package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeAst
import top.wcpe.allincore.dsl.compiler.ast.ThemeValue
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.theme.ThemeDerivation

/**
 * 主题合并 Pass：解析 screen 内 `theme { scheme = "<name>" ... }` 引用的预设，
 * 把 [ThemeRegistry] 中对应的 tokens 作为 baseline 与 screen 显式声明字段合并。
 *
 * 合并策略：baseline 在前 → screen 字段 putAll → screen 字段覆盖 baseline。
 * scheme 字段本身保留在合并结果中作为元信息。
 *
 * 合并后若结果含种子（`seed.*`，无论来自 screen 内联还是引用的预设），再跑一次
 * [ThemeDerivation.derive] 补全 screen 级派生（present-wins，幂等）。
 *
 * scheme 未命中注册表时报 E336 并保留 screen 原 tokens 不动。
 */
class ThemeMergePass(
    private val registry: ThemeRegistry,
    private val diag: DiagnosticCollector,
) {

    fun expand(screen: ScreenAst): ScreenAst {
        val theme = screen.theme ?: return screen
        val schemeName = (theme.tokens["scheme"] as? String)?.takeIf { it.isNotBlank() }
        val hasSeed = ThemeDerivation.hasSeed(theme.tokens)
        // 内联 token 引用别名 / 颜色函数也需解析为字面量，不能短路（否则 ThemeValue 残留到 IR）。
        val hasThemeValue = theme.tokens.values.any { it is ThemeValue }
        // 既不引用 scheme、无种子、也无待解析 ThemeValue：保持原行为，不动 screen。
        if (schemeName == null && !hasSeed && !hasThemeValue) return screen

        val merged = LinkedHashMap<String, Any>()
        val mergedNodes = LinkedHashMap<String, top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst>()
        if (schemeName != null) {
            val baseline = registry.lookup(schemeName)
            if (baseline == null) {
                diag.error(
                    DslError.E001_LEX_ERROR,
                    screen.position,
                    "theme scheme '$schemeName' 未注册",
                )
                return screen
            }
            merged.putAll(baseline)
            mergedNodes.putAll(registry.tokenNodes(schemeName))
        }
        merged.putAll(theme.tokens)
        mergedNodes.putAll(theme.tokenNodes)
        // 先解析 token 引用别名与颜色函数（screen 内联可引用 baseline / 颜色函数求值），再派生。
        val resolved = ThemeValueResolverPass(diag).resolve(merged, mergedNodes, theme.position)
        val finalTokens = if (ThemeDerivation.hasSeed(resolved)) ThemeDerivation.derive(resolved) else resolved
        // screen 内联 theme 不携带文件级目录 metadata；保留 tokenNodes / sourcePosition sidecar。
        val finalTheme = ThemeAst(finalTokens, theme.modifierBundles).also {
            it.copySourceMetadataFrom(theme)
            it.tokenNodes = mergedNodes
        }
        return screen.copy(theme = finalTheme)
    }
}
